var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { json } from "@hyper-hyper-space/hhs3_json";
import { version, walkDelta, computeForkMeet, computeObserverRevisionBound, } from "@hyper-hyper-space/hhs3_mvt";
import { isAuthoredPayload, extractAuthor } from "../../authorship.js";
export class RSetDelta {
    constructor(start, end, revisionBound, root) {
        this.start = start;
        this.end = end;
        this.revisionBound = revisionBound;
        this.type = root.type;
        this.changes = root.changes;
        this.nested = root.nested;
    }
    getRevisionBound() { return this.revisionBound; }
    get added() { return this.changes.added; }
    get removed() { return this.changes.removed; }
    get validityChanges() { return this.changes.validityChanges; }
}
function emptyRSetChanges(type) {
    return {
        type,
        changes: { added: [], removed: [], validityChanges: [] },
        nested: new Map(),
    };
}
// Accumulator for RSet. Per ingested entry it records element-level membership candidates
// and (for permissioned sets) per-entry authorization flips, comparing the start and end
// views once per element. When an entry carries nested ops (inner-elmts), the accumulator
// spawns the child object on the fly, creates its accumulator, unwraps the entry into the
// child's space, and delegates -- self-assembling the nested subtree in finalize.
export class RSetDeltaAccumulator {
    constructor(set, start, end) {
        this.set = set;
        this.start = start;
        this.end = end;
        this.elmtCache = new Set();
        this.added = [];
        this.removed = [];
        this.validityChanges = [];
        this.childAccs = new Map();
    }
    views() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.startView === undefined)
                this.startView = yield this.set.getView(this.start, this.start);
            if (this.endView === undefined)
                this.endView = yield this.set.getView(this.end, this.end);
            return { startView: this.startView, endView: this.endView };
        });
    }
    ingest(entry) {
        return __awaiter(this, void 0, void 0, function* () {
            let changed = false;
            const { startView, endView } = yield this.views();
            const elmts = entry.meta['elmts'];
            if (elmts !== undefined) {
                for (const h of json.fromSet(elmts)) {
                    if (this.elmtCache.has(h))
                        continue;
                    this.elmtCache.add(h);
                    const inStart = yield startView.hasByHash(h);
                    const inEnd = yield endView.hasByHash(h);
                    if (!inStart && inEnd) {
                        this.added.push(h);
                        changed = true;
                    }
                    else if (inStart && !inEnd) {
                        this.removed.push(h);
                        changed = true;
                    }
                }
            }
            if (this.set.isPermissioned()) {
                const p = entry.payload;
                if (p['action'] === 'add' || p['action'] === 'delete') {
                    const wasValid = yield startView.checkEntryAuthorization(entry.hash);
                    const nowValid = yield endView.checkEntryAuthorization(entry.hash);
                    if (wasValid !== nowValid) {
                        const entryElmts = entry.meta['elmts'];
                        if (entryElmts !== undefined) {
                            for (const elementHash of json.fromSet(entryElmts)) {
                                this.validityChanges.push({
                                    entryHash: entry.hash,
                                    elementHash,
                                    action: p['action'],
                                    author: isAuthoredPayload(entry.payload) ? extractAuthor(entry.payload) : undefined,
                                    wasValid,
                                    nowValid,
                                });
                                changed = true;
                            }
                        }
                    }
                }
            }
            const innerElmts = entry.meta['inner-elmts'];
            if (innerElmts !== undefined) {
                for (const childId of json.fromSet(innerElmts)) {
                    const childChanged = yield this.ingestNested(childId, entry);
                    changed = changed || childChanged;
                }
            }
            return changed;
        });
    }
    ingestNested(childId, rawEntry) {
        return __awaiter(this, void 0, void 0, function* () {
            let child = this.childAccs.get(childId);
            if (child === undefined)
                child = yield this.spawnChild(childId);
            // Unwrap the raw entry into the child's scope (NestedScopedDag.loadEntry unwraps
            // payload and meta while preserving the raw hash) before delegating.
            const unwrapped = yield child.scopedDag.loadEntry(rawEntry.hash);
            if (unwrapped === undefined)
                return false;
            return child.acc.ingest(unwrapped);
        });
    }
    spawnChild(childId) {
        return __awaiter(this, void 0, void 0, function* () {
            const contentType = this.set.contentType();
            if (contentType === undefined) {
                throw new Error("Encountered a nested op in an RSet without a contentType");
            }
            const innerFactory = yield this.set.getContext().getRegistry().lookup(contentType);
            const childObj = yield this.set.loadChildObject(innerFactory, childId);
            const acc = childObj.createDeltaAccumulator(this.start, this.end);
            const scopedDag = yield childObj.getScopedDag();
            const entry = { acc, scopedDag };
            this.childAccs.set(childId, entry);
            return entry;
        });
    }
    finalize() {
        return __awaiter(this, void 0, void 0, function* () {
            const nested = new Map();
            for (const [childId, child] of this.childAccs) {
                nested.set(childId, yield child.acc.finalize());
            }
            return {
                type: this.set.getType(),
                changes: {
                    added: this.added,
                    removed: this.removed,
                    validityChanges: this.validityChanges,
                },
                nested,
            };
        });
    }
}
export function computeRSetDelta(set, rawDag, strategy, start, end) {
    return __awaiter(this, void 0, void 0, function* () {
        if (strategy === 'bounded')
            return computeDeltaBounded(set, rawDag, start, end);
        if (strategy === 'full')
            return computeDeltaFull(set, rawDag, start, end);
        throw new Error("Invalid delta strategy: " + strategy);
    });
}
function computeDeltaFull(set, rawDag, start, end) {
    return __awaiter(this, void 0, void 0, function* () {
        // Full scan: empty bound, so the walk visits the entire history of `end`.
        const root = yield walkDelta(rawDag, start, end, version(), set.createDeltaAccumulator(start, end));
        return new RSetDelta(start, end, version(), root);
    });
}
function computeDeltaBounded(set, rawDag, start, end) {
    return __awaiter(this, void 0, void 0, function* () {
        const fork = yield rawDag.findForkPosition(start, end);
        if (fork.forkA.size > 0) {
            throw new Error("bounded computeDelta requires END to extend START");
        }
        if (fork.forkB.size === 0) {
            return new RSetDelta(start, end, fork.commonFrontier, emptyRSetChanges(set.getType()));
        }
        const meet = yield computeForkMeet(rawDag, fork.common);
        let revisionBound = meet;
        if (set.isPermissioned()) {
            const rcap = yield set.loadRCap();
            if (rcap === undefined)
                throw new Error("Cannot load referenced RCap");
            rcap.setDeltaStrategy('bounded');
            revisionBound = yield computeObserverRevisionBound(set, meet, end, rcap);
        }
        const root = yield walkDelta(rawDag, start, end, revisionBound, set.createDeltaAccumulator(start, end));
        return new RSetDelta(start, end, revisionBound, root);
    });
}
